import { MenuOutlined } from "@ant-design/icons";
import { Avatar, Dropdown, Layout, Space, Typography, Button, Tooltip } from "antd";
import React from "react";
import useSize from "../../hooks/useSize";
import { isOfflineUser } from "../../offline";
import AIsearch from "../AllApp/AIsearch";
import { useSelector } from "react-redux";
import { selectUserDoc } from "../../redux/features/userSlice";
import { BugOutlined, BulbOutlined, BulbFilled, LogoutOutlined, SettingOutlined, TranslationOutlined, UserOutlined, SunOutlined, MoonOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { logout } from "../Siderbar";
import { T1 } from "../../appconfig/texts";
import { AppTitle, Logo } from "../../styles/core-components";
import { selectTheme, setTheme } from "../../redux/features/appSlice";
import { useDispatch } from "react-redux";
import { designations, firstCompany, getCurrentUserDesignation } from "../../helper/attribute";
import { FaChalkboardTeacher } from "react-icons/fa";
import { IoAccessibility } from "react-icons/io5";
import { isSustrax, isSustraxAnnual, isSustraxLite } from "../../helper/plans";

const { Header } = Layout;

const AppHeader = ({ setVisible, visible, colorBgContainer, loading }) => {
  const { isMobile, isExtraSmall, isSmall } = useSize();
  const userDoc = useSelector(selectUserDoc);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector(selectTheme);

  // 1. Extract dark mode toggle and translate button handlers for clarity
  const handleToggleDarkMode = () => {
    dispatch(setTheme({ isDarkMode: !isDarkMode }));
  };
  const handleTranslate = () => {
    document.dispatchEvent(new CustomEvent('openTranslate'));
  };

  // Profile dropdown items with additional options
  const profileItems = [
    {
      key: "/profile",
      label: (
        <Space>
          <UserOutlined />
          <T1 pos={"profile.title"} />
        </Space>
      ),
      onClick: () => navigate("/profile"),
    },
    {
      type: 'divider',
    },
    // Admin settings
    ...(getCurrentUserDesignation() === designations.super_admin ? [{
      key: "admin",
      label: (
        <Space>
          <SettingOutlined />
          Admin Settings
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('openSuperAdminModal'));
      },
    }] : []),
    // Report bug
    ...(!isOfflineUser() ? [{
      key: "bug",
      label: (
        <Space>
          <BugOutlined />
          Report a bug
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('openReportBug'));
      },
    }] : []),
    // Request feature
    ...(!isOfflineUser() ? [{
      key: "feature",
      label: (
        <Space>
          <BulbOutlined />
          Request Feature
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('openRequestFeature'));
      },
    }] : []),
    // Dark mode toggle (moved from header)
    {
      key: "darkmode",
      label: (
        <Space>
          {isDarkMode ? <SunOutlined /> : <MoonOutlined />}
          {isDarkMode ? "Light Mode" : "Dark Mode"}
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('toggleDarkMode'));
      },
    },
    // Tutorial
    ...(!isSmall && !isExtraSmall && isSustraxLite() ? [{
      key: "tutorial",
      label: (
        <Space>
          <FaChalkboardTeacher />
          Tutorial
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('openTutorial'));
      },
    }] : []),
    // Accessibility
    {
      key: "accessibility",
      label: (
        <Space>
          <IoAccessibility />
          Accessibility
        </Space>
      ),
      onClick: () => {
        document.dispatchEvent(new CustomEvent('openAccessibility'));
      },
    },
    {
      type: 'divider',
    },
    // Logout
    {
      key: "logout",
      label: (
        <Space>
          <LogoutOutlined />
          <T1 pos={"profile.logout"} />
        </Space>
      ),
      onClick: () => logout(),
    },
  ];


  return (
    <Header
      key={firstCompany()?.title}
      style={{
        padding: "0 16px",
        background: colorBgContainer,
        alignItems: "center",
        justifyContent: "space-between",
        height: "60px",
        display: userDoc?.firstName ? "flex" : "none",
        width: "100%",
        margin: 0,
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 1000,
        borderBottom: "1px solid #f0f0f0"
      }}
    >

      <div style={{ display: "flex", alignItems: "center" }}>
        {isMobile && (
          <MenuOutlined
            style={{ fontSize: "18px", marginRight: "16px" }}
            onClick={() => setVisible(true)}
          />
        )}

        <Dropdown menu={{ items: profileItems }} trigger={["click"]}>
          <div style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            marginLeft: isMobile ? "0" : "10px",
            cursor: "pointer"
          }}>
            <Logo className={"heading-logo"} style={{ zoom: 0.4 }} />
            {!isExtraSmall && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", transform: "translateY(3px)" }}>
                <Typography.Text strong style={{ margin: 0, textAlign: "center", fontSize: "16px" }}>
                  <AppTitle loading={loading} />
                </Typography.Text>
                <Typography.Text style={{ fontSize: "12px", transform: "translateY(-5px)" }}>
                  {firstCompany()?.title}
                </Typography.Text>
              </div>
            )}
          </div>
        </Dropdown>
      </div>

      <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", zoom: 1.1 }}>
        {
          isSustrax(["pro", "ma"]) ? <AIsearch /> : null
        }
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {/* Translate button in header */}
        <Tooltip title="Translate">
          <Button
            size="large"
            type="text"
            icon={<TranslationOutlined />}
            onClick={handleTranslate}
          />
        </Tooltip>
        {/* Profile dropdown with all options, now including dark mode toggle */}
        <Dropdown menu={{ items: profileItems }} trigger={["click"]} className="profile-option">
          <Avatar

            style={{
              cursor: "pointer",
              backgroundColor: "darkgreen",
              marginLeft: "16px"
            }}
            src={userDoc?.photoURL}
            className="notranslate"
          >
            {userDoc?.firstName?.[0]?.toUpperCase() || "U"}
          </Avatar>
        </Dropdown>
      </div>
    </Header>
  );
};

export default AppHeader;
